import React, { useContext, MouseEvent, FC, memo, ReactNode } from 'react';
import { useMutation } from '@apollo/client';
import { Icon } from '@blueprint/ui';
import { parse, stringify } from 'querystring';
import { useSelector } from 'react-redux';

import { filtersValuesSelector } from '../../../reducers/filters';
import { polyencSelector } from '../../../reducers/searchResults';
import { SearchContext } from '../../SearchContext';
import { constructEditSavedSearchFields } from '../../helpers';
import {
    StyledBanner,
    BannerLink,
    BannerLinkContainer,
    CloseButtonStyle
} from './EditSavedSearchBanner.style';
import {
    EDIT_SAVED_SEARCH,
    CREATE_SAVED_SEARCH
} from '../../../queries/search/editSavedSearch';

type Props = {
    onBannerClose?: () => void;
};

export const EditSavedSearchBanner: FC<Props> = ({ onBannerClose }) => {
    const {
        state: {
            userAlertId,
            savedSearchAndAlerts,
            isEditSavedSearchSuccess,
            searchType
        },
        dispatch
    } = useContext(SearchContext);
    const filters = useSelector(filtersValuesSelector);
    const polyenc = useSelector(polyencSelector);

    const onMutationError = () => {
        dispatch({
            type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
            payload: false
        });
    };

    const [editSavedSearchMutation] = useMutation(EDIT_SAVED_SEARCH, {
        onCompleted: (data) => {
            dispatch({
                type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
                payload: data.updateSavedSearch.status === 200
            });
        },
        onError: () => {
            onMutationError();
        }
    });

    const [createSavedSearchMutation] = useMutation(CREATE_SAVED_SEARCH, {
        onCompleted: (data) => {
            dispatch({
                type: 'SET_IS_EDIT_SAVED_SEARCH_SUCCESS',
                payload: data.createNewSavedSearch.status === 200
            });
        },
        onError: () => {
            onMutationError();
        }
    });

    const pathAndQuery = (savedSearchAndAlerts?.uri || '').split('?');
    const query = parse(pathAndQuery[1]);
    delete query.category;
    query.pref = 'savedsearchres';
    const editSavedSearchUrl = `${pathAndQuery[0]}?${stringify(query)}`;

    const onClose = () => {
        dispatch({
            type: 'SET_IS_EDIT_SAVED_SEARCH_BANNER_VISIBLE',
            payload: false
        });
        if (onBannerClose) {
            onBannerClose();
        }
    };

    const onUpdateSavedSearchClick = async (evt: MouseEvent) => {
        evt.preventDefault();

        const input = constructEditSavedSearchFields({
            userAlertId,
            searchType,
            filters,
            polyenc
        });

        editSavedSearchMutation({
            variables: {
                input
            }
        });
    };

    const onSaveAsNewSearchClicked = async (evt: MouseEvent) => {
        evt.preventDefault();

        const input = constructEditSavedSearchFields({
            userAlertId,
            searchType,
            filters,
            createNew: true,
            polyenc
        });

        createSavedSearchMutation({
            variables: {
                input
            }
        });
    };

    if (isEditSavedSearchSuccess === null) {
        return (
            <MemoizedStyledBanner
                strapline="Saved search"
                title="You updated your search"
                text="Would you like to update your saved search to match these changes?"
            >
                <BannerLinkContainer>
                    <BannerLink
                        variant="tertiary"
                        href={editSavedSearchUrl}
                        onClick={onUpdateSavedSearchClick}
                    >
                        Update
                    </BannerLink>
                    <BannerLink
                        variant="tertiary"
                        href={
                            savedSearchAndAlerts
                                ? `${savedSearchAndAlerts.uri}&frequency=`
                                : ''
                        }
                        onClick={onSaveAsNewSearchClicked}
                    >
                        Save as new search
                    </BannerLink>
                    <CloseButton onClick={onClose} />
                </BannerLinkContainer>
            </MemoizedStyledBanner>
        );
    } else if (isEditSavedSearchSuccess) {
        return (
            <MemoizedStyledBanner
                strapline="Success"
                title="Your search has been saved"
                text=""
                type="success"
            >
                <CloseButton onClick={onClose} />
            </MemoizedStyledBanner>
        );
    } else if (!isEditSavedSearchSuccess) {
        return (
            <MemoizedStyledBanner
                strapline="An error occurred"
                title="Sorry, we weren't able to update your search"
                text="Would you like to try again or save as a new search"
                type="failure"
            >
                <BannerLinkContainer>
                    <BannerLink
                        variant="tertiary"
                        href={editSavedSearchUrl}
                        onClick={onUpdateSavedSearchClick}
                    >
                        Update
                    </BannerLink>
                    <BannerLink
                        variant="tertiary"
                        href={
                            savedSearchAndAlerts
                                ? `${savedSearchAndAlerts.uri}&frequency=`
                                : ''
                        }
                    >
                        Save as new search
                    </BannerLink>
                    <CloseButton onClick={onClose} />
                </BannerLinkContainer>
            </MemoizedStyledBanner>
        );
    }
    return null;
};

type BannerProps = {
    strapline: string;
    title: string;
    text?: string;
    type?: 'alert' | 'success' | 'failure';
    children: ReactNode;
};

const CloseButton: FC<{ onClick: () => void }> = ({ onClick }) => (
    <CloseButtonStyle
        type="button"
        onClick={onClick}
        aria-label="close"
        data-testid="edit-saved-search-banner-close-button"
    >
        <Icon iconId="close" size="small" />
    </CloseButtonStyle>
);

const MemoizedStyledBanner = memo<BannerProps>(
    ({ strapline, title, text = '', type = 'alert', children }) => {
        return (
            <StyledBanner
                strapline={strapline}
                straplineColor="text-title"
                title={title}
                smallTitle
                text={text}
                iconSize="large"
                type={type}
            >
                {children}
            </StyledBanner>
        );
    }
);
MemoizedStyledBanner.displayName = 'MemoizedStyledBanner';
